import { Location, type UUID } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import Alert from '@cobliteam/ui/components/Alert';
import type { EventSourcePolyfill } from 'event-source-polyfill';
import queryString from 'query-string';
import { EventSourceRequester } from '../../request/EventSourceRequester';
import type { Configuration } from '../../request/Types';
import { deviceStatusEventsTransformer } from './DeviceTransformer';
import type { DeviceStatusEvent, DeviceStatusEventAPIV2, LastLocationAPI } from './definitions';

const DEFAULT_RETRY_TIMEOUT_IN_SECONDS = 3;
const DEFAULT_DEVICES_SLIDE_SSE_TIME_SECONDS = 5;

export class FleetEventSourceRequesterV2 {
  private static enableV2FleetEvents = false;
  private eventSourceRequester: EventSourceRequester;
  private devicesSlideSseTimeSeconds: number;

  private numberOfRetries = 0;
  private currentUrl?: string;

  constructor(configuration: Configuration & { useEventSourceLibrary: boolean; devicesSlideSseTimeSeconds: number }) {
    this.eventSourceRequester = new EventSourceRequester(configuration);
    this.devicesSlideSseTimeSeconds = configuration.devicesSlideSseTimeSeconds || DEFAULT_DEVICES_SLIDE_SSE_TIME_SECONDS;
  }

  public static setEnableV2FleetEvents = (enableV2FleetEvents: boolean) => {
    this.enableV2FleetEvents = enableV2FleetEvents;
  };

  public setDevicesSlideSseTimeSeconds = (devicesSlideSseTimeSeconds: number) => {
    this.devicesSlideSseTimeSeconds = devicesSlideSseTimeSeconds;
  };

  public subscribeToDeviceStatus = (fleetId: UUID, callback: (data: DeviceStatusEvent[]) => void, vehicleGroupIds?: UUID[]): Promise<EventSource | EventSourcePolyfill> => {
    const queryParams: Record<string, string | number> = {
      refresh_interval_in_seconds: this.devicesSlideSseTimeSeconds,
    };

    if (vehicleGroupIds && vehicleGroupIds.length > 0) {
      queryParams.wanted_vehicle_group_ids = vehicleGroupIds.join(',');
    }

    const url = `/v2/fleet-events/${fleetId}?${queryString.stringify(queryParams)}`;
    this.currentUrl = url;
    let reconnectSSE: any;

    return this.eventSourceRequester.subscribe({ url }).then((eventSource) => {
      eventSource.onerror = (err: Event) => {
        // when 10 retries, send an error to Datadog
        if (this.numberOfRetries % 10 === 0) {
          const errorMessage = `Connection failed, error details: type: ${err.type}, target: ${JSON.stringify(err.target)}, error: ${JSON.stringify(err)}`;
          const ddError = new Error(errorMessage);
          ddError.name = 'Fleet-events-v2';
          DatadogTrack.error(ddError);
        }

        // Release from pool before closing to prevent memory leak
        if (this.currentUrl) {
          this.eventSourceRequester.unsubscribe(this.currentUrl);
        }

        eventSource.close();

        const currentLocation = window.location.href;
        const currentLocationIsMonitoring = currentLocation.includes(Location.MONITORING);

        if (currentLocationIsMonitoring && FleetEventSourceRequesterV2.enableV2FleetEvents) {
          reconnectSSE = setTimeout(
            () => {
              this.subscribeToDeviceStatus(fleetId, callback, vehicleGroupIds);
              this.numberOfRetries += 1;
            },
            DEFAULT_RETRY_TIMEOUT_IN_SECONDS ** this.numberOfRetries * 1000
          );
        }
      };

      eventSource.onopen = () => {
        DatadogTrack.action('Fleet-events-v2 connection open successfully');
        clearTimeout(reconnectSSE);
        this.numberOfRetries = 0;
      };

      // @ts-ignore
      eventSource.addEventListener('DeviceStatus[]', (evt: any) => {
        try {
          const translatedEventData: LastLocationAPI[] = this.translateEvent(evt);
          const deviceStatusData: DeviceStatusEvent[] = deviceStatusEventsTransformer(translatedEventData);

          if (callback) {
            callback(deviceStatusData);
          }
        } catch {
          Alert.error('Erro ao receber as informações de localização em tempo real');
        }
      });

      return eventSource;
    });
  };

  private translateEvent = (evt: any): LastLocationAPI[] => {
    const parsedEventData: DeviceStatusEventAPIV2[] = JSON.parse(evt.data);

    return parsedEventData.map((event) => {
      const eventTimeInMillis = new Date(event.event_time).getTime();

      const translatedEventData: LastLocationAPI = {
        ...event,
        speed: event.speed_in_kmh,
        time: eventTimeInMillis,
        time_in_millis: eventTimeInMillis,
        cobli_ingested_at: new Date(event.cobli_ingested_at).getTime(),
        processed_at: new Date(event.processed_at).getTime(),
      };

      return translatedEventData;
    });
  };

  public unsubscribe = () => {
    if (this.currentUrl) {
      this.eventSourceRequester.unsubscribe(this.currentUrl);
      this.currentUrl = undefined;
    }
  };
}

export default FleetEventSourceRequesterV2;
